import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { formatPrice } from "@/lib/api";

export default function EMICalc({ defaultLoan = 5000000 }) {
  const [loan, setLoan] = useState(defaultLoan);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, total, interest } = useMemo(() => {
    const P = Number(loan) || 0;
    const r = (Number(rate) || 0) / 12 / 100;
    const n = (Number(years) || 0) * 12;
    if (P <= 0 || n <= 0) return { emi: 0, total: 0, interest: 0 };
    const emi = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    return { emi, total, interest: total - P };
  }, [loan, rate, years]);

  return (
    <div className="grid gap-6 md:grid-cols-2 items-start" data-testid="emi-calculator">
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm">Loan amount</Label>
            <span className="text-sm font-heading font-bold text-forest">{formatPrice(loan)}</span>
          </div>
          <Slider
            data-testid="emi-loan-slider"
            value={[loan]}
            min={500000}
            max={30000000}
            step={100000}
            onValueChange={(v) => setLoan(v[0])}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm">Interest rate</Label>
            <span className="text-sm font-heading font-bold text-forest">{rate}%</span>
          </div>
          <Slider data-testid="emi-rate-slider" value={[rate]} min={6} max={12} step={0.1} onValueChange={(v) => setRate(v[0])} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm">Tenure</Label>
            <span className="text-sm font-heading font-bold text-forest">{years} yrs</span>
          </div>
          <Slider data-testid="emi-tenure-slider" value={[years]} min={5} max={30} step={1} onValueChange={(v) => setYears(v[0])} />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-cream p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Monthly EMI</div>
        <div className="font-heading text-4xl md:text-5xl font-extrabold text-forest mt-1" data-testid="emi-monthly-value">
          {formatPrice(Math.round(emi))}
        </div>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div>
            <div className="text-xs text-muted-foreground">Principal</div>
            <div className="text-lg font-semibold text-forest mt-0.5">{formatPrice(loan)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total interest</div>
            <div className="text-lg font-semibold text-forest mt-0.5">{formatPrice(Math.round(interest))}</div>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-muted-foreground">Total payment</div>
            <div className="text-lg font-semibold text-forest mt-0.5">{formatPrice(Math.round(total))}</div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">
          Indicative EMI. Actual rate depends on your profile. We work with multiple banks & NBFCs — get a pre-approved offer in 48 hours.
        </p>
      </div>
    </div>
  );
}
